import { parseUnits } from 'ethers';

import { erc20Abi, erc20AbiEthers, factoryAbi, factoryAbiEthers } from '../shared/abi';
import { readClient } from '../shared/client';
import { DEPLOYMENT, getActiveChainConfig, getActiveNetworkKey, setActiveNetwork, type NetworkKey } from '../shared/config';
import { fmtToken, shortAddr } from '../shared/format';
import { resolveCollateralMeta, resolveTokenLogoUrl, resolveTokenLogoUrlSync } from '../shared/collateral';
import { getTokensForChain, type DexTopToken } from '../shared/tokenList';
import { logLine } from '../shared/logger';
import { openAccountModal } from '../shared/accountModal';
import { friendlyErrorMessage } from '../market/errorHandling';
import {
  bindWalletEvents,
  connectWallet,
  contractWithSigner,
  ensurePulseChainConnected,
  rehydrateWalletSession,
  walletState,
} from '../shared/wallet';

const MAX_OUTCOME_TOKEN_LABEL_BYTES = 25;
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
const MIN_INITIAL_LIQUIDITY_DEFAULT = 50_000_000n; // 50 USDC (6 decimals)
const MIN_INITIAL_LIQUIDITY_WHOLE_TOKENS = 50n;
const MAINNET_DAI_ADDRESS = '0xefD766cCb38EaF1dfd701853BFCe31359239F305';
const LOCAL_COIN_FALLBACK = `${import.meta.env.BASE_URL}images/coin.svg`;

export function mountCreateView(app: HTMLElement): void {
  const activeChain = getActiveChainConfig();
  const activeNetwork = getActiveNetworkKey();
  const toastSink = document.createElement('div');

  const notify = (kind: 'ok' | 'warn' | 'err', message: string) => {
    logLine(toastSink, kind, message);
  };

  app.innerHTML = `
  <div class="app create-page">
    <header class="topbar">
      <div class="brand">
        <div class="brand-mark" aria-hidden="true"></div>
        <div>
          <div class="brand-text">Create Market</div>
          <div class="brand-sub">${activeChain.chainName} · ${activeChain.id}</div>
        </div>
      </div>
      <div class="header-actions">
        <a class="btn header-back-btn" href="${import.meta.env.BASE_URL}" aria-label="Back to markets" title="Back to markets">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
        <details class="header-dropdown" id="networkDropdown">
          <summary class="header-dropdown-btn" aria-label="Network selector">
            <svg class="header-dropdown-trigger-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <circle cx="12" cy="12" r="9" fill="none" stroke-width="2"/>
              <path d="M3 12h18M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" fill="none" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span class="header-dropdown-caret" aria-hidden="true">▾</span>
          </summary>
          <div class="header-dropdown-menu" role="menu" aria-label="Network selector">
            <button id="networkMainnetBtn" class="header-dropdown-item ${activeNetwork === 'mainnet' ? 'active' : ''}" type="button" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2v20M2 12h20" fill="none" stroke-width="2" stroke-linecap="round"/></svg>
                Mainnet
              </span>
            </button>
            <button id="networkTestnetBtn" class="header-dropdown-item ${activeNetwork === 'testnet' ? 'active' : ''}" type="button" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 6h16v12H4zM8 10h8" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Testnet
              </span>
            </button>
          </div>
        </details>
        <details class="header-dropdown" id="linksDropdown">
          <summary class="header-dropdown-btn" aria-label="Header links">
            <svg class="header-dropdown-trigger-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" fill="none" stroke-width="2" stroke-linejoin="round"/>
              <path d="M4 7.5l8 4.5 8-4.5M12 12v9" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="header-dropdown-caret" aria-hidden="true">▾</span>
          </summary>
          <div class="header-dropdown-menu" role="menu" aria-label="Navigation links">
            <a href="/boxbet/help" class="header-dropdown-item" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 18h.01M9.1 9a3 3 0 1 1 5.8 1c-.57.78-1.55 1.23-2.18 2" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Help
              </span>
            </a>
            <a href="/boxbet/adversary" class="header-dropdown-item" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l8 4v5c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V7l8-4z" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Adversary
              </span>
            </a>
          </div>
        </details>
        <button id="connectBtn" class="wallet-pill">Connect Wallet</button>
      </div>
    </header>

    <section class="create-shell">
      <article class="card create-main-card">
        <div class="create-head-row">
          <div>
            <div class="section-title">Create Market</div>
            <div class="mono create-subtitle">Dashboard flow with funding checks and confirmations.</div>
          </div>
        </div>

        <div class="create-grid">
          <div class="create-panel card">
            <div class="mono create-panel-title">Market Setup</div>

            <label class="mono create-label" title="The bet statement users are forecasting.">
              BET <span class="create-tip" aria-hidden="true" title="Describe the forecast question clearly.">i</span>
            </label>
            <input id="q" class="btn create-input" placeholder="Will this event happen?" />

            <div class="create-two-col">
              <div>
                <label class="mono create-label" title="Display name for outcome token 1.">
                  Outcome 1 Token Name <span class="create-tip" aria-hidden="true" title="Shown in market cards and trade view.">i</span>
                </label>
                <input id="yesName" class="btn create-input" maxlength="64" placeholder="YEAH" />
              </div>
              <div>
                <label class="mono create-label" title="Ticker symbol for outcome token 1.">
                  Outcome 1 Token Symbol <span class="create-tip" aria-hidden="true" title="1 to 25 UTF-8 bytes.">i</span>
                </label>
                <input id="yesSymbol" class="btn create-input" maxlength="64" placeholder="YEH" />
              </div>
            </div>

            <div class="create-two-col">
              <div>
                <label class="mono create-label" title="Display name for outcome token 2.">
                  Outcome 2 Token Name <span class="create-tip" aria-hidden="true" title="Shown in market cards and trade view.">i</span>
                </label>
                <input id="noName" class="btn create-input" maxlength="64" placeholder="NAH" />
              </div>
              <div>
                <label class="mono create-label" title="Ticker symbol for outcome token 2.">
                  Outcome 2 Token Symbol <span class="create-tip" aria-hidden="true" title="1 to 25 UTF-8 bytes.">i</span>
                </label>
                <input id="noSymbol" class="btn create-input" maxlength="64" placeholder="NAH" />
              </div>
            </div>

            <div class="create-two-col">
              <div>
                <label class="mono create-label" title="Hours from now until resolution window opens.">
                  Resolve In Hours <span class="create-tip" aria-hidden="true" title="Minimum 1 hour.">i</span>
                </label>
                <input id="h" type="number" class="btn create-input" value="24" min="1" step="1" />
              </div>
              <div>
                <label class="mono create-label" title="Starting initial liquidity amount.">
                  Initial Liquidity <span class="create-tip" aria-hidden="true" title="Collateral required is 2x liquidity.">i</span>
                </label>
                <input id="liq" type="number" class="btn create-input" value="100000" min="1" step="1" />
              </div>
            </div>

          </div>

          <aside class="create-panel card create-funding-panel">
            <div class="create-funding-head">
              <div class="mono create-panel-title">Create Market Funding</div>
              <div class="create-collateral-badge" title="Selected collateral token">
                <img id="createCollateralLogo" class="token-logo" src="${import.meta.env.BASE_URL}images/coin.svg" alt="Collateral" />
                <span id="createCollateralBadgeSymbol" class="mono">COLLATERAL</span>
              </div>
            </div>

            <div class="create-collateral-config">
              <label class="mono create-label" title="Choose default collateral or custom collateral token.">
                Collateral Choice <span class="create-tip" aria-hidden="true" title="Default uses deployed collateral. Custom requires 18-dec token.">i</span>
              </label>
              <div class="network-toggle" role="group" aria-label="Collateral mode selector">
                <button id="collateralDefaultBtn" class="network-toggle-btn active" type="button">Default</button>
                <button id="collateralCustomBtn" class="network-toggle-btn" type="button">Custom</button>
              </div>

              <div id="customCollateralField" style="display:none;">
                <label class="mono create-label" title="ERC-20 collateral token address for custom market.">
                  Custom Collateral Token <span class="create-tip" aria-hidden="true" title="Must be a valid 18-decimal ERC-20 address.">i</span>
                </label>
                <div id="tokenListPickerWrap" class="create-token-picker" style="margin-bottom:6px;">
                  <button id="tokenListTrigger" type="button" class="btn create-input create-token-list-trigger" aria-haspopup="listbox" aria-expanded="false">
                    <span class="create-token-trigger-left">
                      <img id="tokenListTriggerLogo" class="token-logo" src="${import.meta.env.BASE_URL}images/coin.svg" alt="Selected token" />
                      <span class="create-token-trigger-text">
                        <span id="tokenListTriggerSymbol" class="mono">TOKEN</span>
                        <span id="tokenListTriggerName" class="mono">Pick from mainnet list</span>
                      </span>
                    </span>
                    <span class="create-token-trigger-caret" aria-hidden="true">▾</span>
                  </button>
                  <div id="tokenListMenu" class="create-token-list-menu" role="listbox" style="display:none;"></div>
                </div>
                <input id="customCollateralInput" class="btn create-input" placeholder="0x..." />
              </div>
            </div>

            <div class="create-metric" title="Collateral balance in connected wallet.">
              <span id="walletCollateralLabel" class="mono">Wallet Collateral</span>
              <span id="walletUsdcCreate">-</span>
            </div>
            <div class="create-metric" title="Required collateral transferred to factory.">
              <span id="requiredCollateralLabel" class="mono">Required Collateral</span>
              <span id="requiredCollateralCreate">-</span>
            </div>
            <div class="create-metric" title="Current collateral allowance granted to factory.">
              <span id="factoryAllowanceLabel" class="mono">Factory Allowance</span>
              <span id="factoryAllowanceCreate">-</span>
            </div>

            <div id="createFundingStatus" class="mono create-funding-status">Connect wallet</div>

            <div class="mono create-collateral-note">
              Factory will transfer <span id="requiredCollateral">200000</span> <span id="requiredCollateralSymbol">USDC</span> collateral (2x initial liquidity) from your wallet.
            </div>

            <div class="create-actions">
              <a class="btn" href="${import.meta.env.BASE_URL}">Cancel</a>
              <button id="confirmCreate" class="btn btn-primary" type="button">Submit</button>
            </div>
          </aside>
        </div>
      </article>

      <article class="card" style="margin-top: 16px; padding: 20px; background: rgba(7, 16, 26, 0.6); border-color: var(--line);">
        <div class="mono" style="font-size: 0.7rem; color: var(--info); letter-spacing: 0.04em; margin-bottom: 10px; text-transform: uppercase;">Creator Explainer</div>

        <div style="display: grid; gap: 10px; color: var(--muted); line-height: 1.5; font-size: 0.82rem; padding: 0 6px;">
          <div>
            <div style="font-weight: 700; color: #d5e9ff; margin-bottom: 4px; font-size: 0.85rem;">Your Role as Creator</div>
            <p style="margin: 0; font-size: 0.78rem;">
              You own this market and are responsible for managing its lifecycle. You decide which outcome proposals to accept, 
              handle disputes if they arise, and ensure the market settles fairly. Users trust you to run an honest market and be available 
              during resolution.
            </p>
          </div>

          <div>
            <div style="font-weight: 700; color: #d5e9ff; margin-bottom: 4px; font-size: 0.85rem;">How Resolution Works</div>
            <p style="margin: 0; margin-bottom: 4px; font-size: 0.78rem;">
              <strong>Early proposal phase (before resolve time):</strong> anyone can submit YES or NO proposals. As creator, you review and choose whether to accept or decline.
            </p>
            <ul style="margin: 0; padding-left: 18px; font-size: 0.78rem;">
              <li style="margin-bottom: 2px;"><strong>Accept (early):</strong> proposal becomes the accepted outcome, trading pauses, and dispute/finalization timers begin.</li>
              <li style="margin-bottom: 2px;"><strong>Decline (early):</strong> proposal is removed, proposer bond is refunded, and new proposals can still be submitted.</li>
              <li><strong>After resolve time:</strong> outcome submissions are no longer creator-gated in the same way; unresolved markets move through dispute/finalization rules from the accepted submission state.</li>
            </ul>
          </div>

          <div>
            <div style="font-weight: 700; color: #d5e9ff; margin-bottom: 4px; font-size: 0.85rem;">Dispute Handling</div>
            <p style="margin: 0; margin-bottom: 4px; font-size: 0.78rem;">
              If someone disputes your accepted proposal, you have 24 hours to decide the final outcome:
            </p>
            <ul style="margin: 0; padding-left: 18px; font-size: 0.78rem;">
              <li style="margin-bottom: 2px;"><strong>Keep accepted outcome:</strong> Finalize to that outcome. Disputer loses their bond (goes to winners' pool).</li>
              <li style="margin-bottom: 2px;"><strong>Choose disputed outcome:</strong> Finalize to disputer's choice. Their bond is returned.</li>
              <li><strong>Miss the deadline:</strong> Market auto-finalizes as Invalid (neutral washout). Both sides reclaim pro-rata.</li>
            </ul>
          </div>

          <div>
            <div style="font-weight: 700; color: #d5e9ff; margin-bottom: 4px; font-size: 0.85rem;">LP (Liquidity Provider) Rules</div>
            <p style="margin: 0; margin-bottom: 4px; font-size: 0.78rem;">
              You provide the initial liquidity (2x your specified amount):
            </p>
            <ul style="margin: 0; padding-left: 18px; font-size: 0.78rem;">
              <li style="margin-bottom: 2px;">Both reserves start equal. As users trade, reserves shift, reflecting market sentiment.</li>
              <li style="margin-bottom: 2px;"><strong>First to market:</strong> Buy some YES and NO shares yourself early. This creates a small profit buffer if your market is wrong, and shows skin-in-the-game to traders.</li>
              <li>At settlement, unclaimed fees and bonds from losing/disputed proposals flow back to the winner pool, increasing payout per winning share.</li>
            </ul>
          </div>

          <div>
            <div style="font-weight: 700; color: #d5e9ff; margin-bottom: 4px; font-size: 0.85rem;">Why Buy Both Sides?</div>
            <p style="margin: 0; font-size: 0.78rem;">
              Buying a few YES and NO shares upfront does two things: (1) You profit if trading volume grows the total pool, 
              even if the outcome isn't favorable to your personal bet, and (2) It signals to users that you believe the market is fair and worth participating in. 
              Combined with fees flowing to the house address, this gives you incentive to run a liquid, honest market.
            </p>
          </div>
        </div>
      </article>
    </section>
  </div>
  `;

  const connectBtn = app.querySelector('#connectBtn') as HTMLButtonElement;
  const networkMainnetBtn = app.querySelector('#networkMainnetBtn') as HTMLButtonElement;
  const networkTestnetBtn = app.querySelector('#networkTestnetBtn') as HTMLButtonElement;
  const networkDropdown = app.querySelector('#networkDropdown') as HTMLDetailsElement;
  const linksDropdown = app.querySelector('#linksDropdown') as HTMLDetailsElement;

  const qInput = app.querySelector('#q') as HTMLInputElement;
  const yesNameInput = app.querySelector('#yesName') as HTMLInputElement;
  const yesSymbolInput = app.querySelector('#yesSymbol') as HTMLInputElement;
  const noNameInput = app.querySelector('#noName') as HTMLInputElement;
  const noSymbolInput = app.querySelector('#noSymbol') as HTMLInputElement;
  const hoursInput = app.querySelector('#h') as HTMLInputElement;
  const liquidityInput = app.querySelector('#liq') as HTMLInputElement;
  const collateralDefaultBtn = app.querySelector('#collateralDefaultBtn') as HTMLButtonElement;
  const collateralCustomBtn = app.querySelector('#collateralCustomBtn') as HTMLButtonElement;
  const customCollateralField = app.querySelector('#customCollateralField') as HTMLDivElement;
  const customCollateralInput = app.querySelector('#customCollateralInput') as HTMLInputElement;

  const requiredCollateralEl = app.querySelector('#requiredCollateral') as HTMLElement;
  const requiredCollateralSymbolEl = app.querySelector('#requiredCollateralSymbol') as HTMLElement;
  const createCollateralLogoEl = app.querySelector('#createCollateralLogo') as HTMLImageElement;
  const createCollateralBadgeSymbolEl = app.querySelector('#createCollateralBadgeSymbol') as HTMLElement;
  const walletCollateralLabelEl = app.querySelector('#walletCollateralLabel') as HTMLElement;
  const requiredCollateralLabelEl = app.querySelector('#requiredCollateralLabel') as HTMLElement;
  const factoryAllowanceLabelEl = app.querySelector('#factoryAllowanceLabel') as HTMLElement;
  const walletUsdcCreateEl = app.querySelector('#walletUsdcCreate') as HTMLElement;
  const requiredCollateralCreateEl = app.querySelector('#requiredCollateralCreate') as HTMLElement;
  const factoryAllowanceCreateEl = app.querySelector('#factoryAllowanceCreate') as HTMLElement;
  const createFundingStatusEl = app.querySelector('#createFundingStatus') as HTMLElement;
  const confirmCreateBtn = app.querySelector('#confirmCreate') as HTMLButtonElement;
  const tokenListPickerWrapEl = app.querySelector('#tokenListPickerWrap') as HTMLDivElement;
  const tokenListTriggerEl = app.querySelector('#tokenListTrigger') as HTMLButtonElement;
  const tokenListTriggerLogoEl = app.querySelector('#tokenListTriggerLogo') as HTMLImageElement;
  const tokenListTriggerSymbolEl = app.querySelector('#tokenListTriggerSymbol') as HTMLElement;
  const tokenListTriggerNameEl = app.querySelector('#tokenListTriggerName') as HTMLElement;
  const tokenListMenuEl = app.querySelector('#tokenListMenu') as HTMLDivElement;
  const showTokenListPicker = activeNetwork === 'mainnet';
  const tokenMetaByAddress = new Map<string, DexTopToken>();
  let availableTokenOptions: DexTopToken[] = [];
  let collateralMode: 'default' | 'custom' = 'default';

  function setTokenMenuOpen(next: boolean) {
    tokenListMenuEl.style.display = next ? '' : 'none';
    tokenListTriggerEl.setAttribute('aria-expanded', next ? 'true' : 'false');
  }

  function setTokenTriggerPresentation(address: string) {
    const token = tokenMetaByAddress.get(address.trim().toLowerCase());
    if (!token) {
      tokenListTriggerLogoEl.src = LOCAL_COIN_FALLBACK;
      tokenListTriggerLogoEl.alt = 'Selected token';
      tokenListTriggerSymbolEl.textContent = 'CUSTOM';
      tokenListTriggerNameEl.textContent = address.trim() || 'Pick from mainnet list';
      return;
    }
    tokenListTriggerLogoEl.src = resolveTokenLogoUrlSync(token.address) || LOCAL_COIN_FALLBACK;
    tokenListTriggerLogoEl.onerror = () => {
      tokenListTriggerLogoEl.onerror = null;
      tokenListTriggerLogoEl.src = LOCAL_COIN_FALLBACK;
    };
    tokenListTriggerLogoEl.alt = token.symbol;
    tokenListTriggerSymbolEl.textContent = token.symbol;
    tokenListTriggerNameEl.textContent = token.name;
  }

  function renderTokenListOptions(tokens: DexTopToken[]) {
    tokenListMenuEl.innerHTML = '';
    for (const token of tokens) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'create-token-option';
      button.setAttribute('role', 'option');
      button.dataset.address = token.address;
      button.innerHTML = `
        <img class="token-logo" src="${resolveTokenLogoUrlSync(token.address) || LOCAL_COIN_FALLBACK}" alt="${token.symbol}" onerror="this.onerror=null;this.src='${LOCAL_COIN_FALLBACK}'" />
        <span class="create-token-option-main">
          <span class="mono create-token-option-symbol">${token.symbol}</span>
          <span class="mono create-token-option-name">${token.name}</span>
        </span>
        <span class="mono create-token-option-address">${shortAddr(token.address)}</span>
      `;
      tokenListMenuEl.appendChild(button);
    }
  }

  tokenListPickerWrapEl.style.display = showTokenListPicker ? '' : 'none';
  tokenListTriggerEl.disabled = !showTokenListPicker;
  if (!showTokenListPicker) {
    setTokenMenuOpen(false);
  }

  // Populate token-picker asynchronously once the list is available.
  if (showTokenListPicker) {
    void (async () => {
      try {
        const chainId = getActiveChainConfig().id;
        tokenListMenuEl.innerHTML = '<div class="create-token-list-empty mono">Loading mainnet token list...</div>';
        const tokens = (await getTokensForChain(chainId)).filter((token) => Number(token.chainId) === 369 && Number(token.decimals) === 18);
        if (tokens.length === 0) {
          const fallbackOption: DexTopToken = {
            address: MAINNET_DAI_ADDRESS,
            chainId: 369,
            decimals: 18,
            logoURI: '',
            name: 'Dai-FETH',
            symbol: 'DAI',
          };
          tokenMetaByAddress.set(MAINNET_DAI_ADDRESS.toLowerCase(), fallbackOption);
          availableTokenOptions = [fallbackOption];
          renderTokenListOptions(availableTokenOptions);
          customCollateralInput.value = MAINNET_DAI_ADDRESS;
          setTokenTriggerPresentation(MAINNET_DAI_ADDRESS);
          return;
        }

        for (const token of tokens) {
          tokenMetaByAddress.set(token.address.toLowerCase(), token);
        }
        availableTokenOptions = tokens;
        renderTokenListOptions(availableTokenOptions);

        const daiFromList = tokens.find((token) => token.address.toLowerCase() === MAINNET_DAI_ADDRESS.toLowerCase());
        const defaultToken = daiFromList ?? tokens[0];
        customCollateralInput.value = defaultToken.address;
        setTokenTriggerPresentation(defaultToken.address);
      } catch {
        const fallbackOption: DexTopToken = {
          address: MAINNET_DAI_ADDRESS,
          chainId: 369,
          decimals: 18,
          logoURI: '',
          name: 'Dai-FETH',
          symbol: 'DAI',
        };
        tokenMetaByAddress.set(MAINNET_DAI_ADDRESS.toLowerCase(), fallbackOption);
        availableTokenOptions = [fallbackOption];
        renderTokenListOptions(availableTokenOptions);
        customCollateralInput.value = MAINNET_DAI_ADDRESS;
        setTokenTriggerPresentation(MAINNET_DAI_ADDRESS);
      }
    })();

    tokenListTriggerEl.addEventListener('click', () => {
      if (!availableTokenOptions.length) return;
      const isOpen = tokenListMenuEl.style.display !== 'none';
      setTokenMenuOpen(!isOpen);
    });

    tokenListMenuEl.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const option = target.closest('.create-token-option') as HTMLButtonElement | null;
      if (!option?.dataset.address) return;
      const selected = option.dataset.address;
      customCollateralInput.value = selected;
      setTokenTriggerPresentation(selected);
      setTokenMenuOpen(false);
      void refreshCreateBalances();
    });
  }

  customCollateralInput.addEventListener('input', () => {
    setTokenTriggerPresentation(customCollateralInput.value);
  });

  function onNetworkToggle(next: NetworkKey) {
    if (next === getActiveNetworkKey()) return;
    setActiveNetwork(next);
    window.location.reload();
  }

  function positionHeaderDropdown(dropdown: HTMLDetailsElement | null): void {
    if (!dropdown || !dropdown.open) return;
    const summary = dropdown.querySelector('summary') as HTMLElement | null;
    const menu = dropdown.querySelector('.header-dropdown-menu') as HTMLElement | null;
    if (!summary || !menu) return;
    const viewportPadding = 8;
    const summaryRect = summary.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const centeredLeft = summaryRect.left + (summaryRect.width / 2) - (menuRect.width / 2);
    const maxLeft = Math.max(viewportPadding, window.innerWidth - viewportPadding - menuRect.width);
    const clampedLeft = Math.min(Math.max(viewportPadding, centeredLeft), maxLeft);
    menu.style.left = `${clampedLeft - summaryRect.left}px`;
    menu.style.right = 'auto';
    menu.style.transform = 'none';
  }

  function refreshHeaderDropdownPositions(): void {
    positionHeaderDropdown(networkDropdown);
    positionHeaderDropdown(linksDropdown);
  }

  const scheduleHeaderDropdownRefresh = (): void => {
    window.requestAnimationFrame(refreshHeaderDropdownPositions);
  };

  networkMainnetBtn.addEventListener('click', () => onNetworkToggle('mainnet'));
  networkTestnetBtn.addEventListener('click', () => onNetworkToggle('testnet'));
  networkDropdown.addEventListener('toggle', scheduleHeaderDropdownRefresh);
  linksDropdown.addEventListener('toggle', scheduleHeaderDropdownRefresh);
  window.addEventListener('resize', refreshHeaderDropdownPositions);
  document.addEventListener('click', (event) => {
    const target = event.target as Node;
    if (!networkDropdown.contains(target)) networkDropdown.open = false;
    if (!linksDropdown.contains(target)) linksDropdown.open = false;
    if (!tokenListPickerWrapEl.contains(target)) setTokenMenuOpen(false);
  });

  function setAccountLabel() {
    connectBtn.textContent = walletState.account ? shortAddr(walletState.account) : 'Connect Wallet';
    connectBtn.classList.toggle('connected', Boolean(walletState.account));
  }

  function updateRequiredCollateralLabel() {
    const liq = Number(liquidityInput.value || '0');
    const required = Number.isFinite(liq) && liq > 0 ? liq * 2 : 0;
    requiredCollateralEl.textContent = String(Math.floor(required));
  }

  async function updateCreateCollateralBadge(address: string, symbol: string) {
    const logo = await resolveTokenLogoUrl(address);
    createCollateralLogoEl.src = logo ?? `${import.meta.env.BASE_URL}images/coin.svg`;
    createCollateralLogoEl.alt = symbol;
    createCollateralBadgeSymbolEl.textContent = symbol;
  }

  function setCollateralMode(mode: 'default' | 'custom') {
    collateralMode = mode;
    const isCustom = mode === 'custom';
    collateralDefaultBtn.classList.toggle('active', !isCustom);
    collateralCustomBtn.classList.toggle('active', isCustom);
    customCollateralField.style.display = isCustom ? '' : 'none';
  }

  function isAddress(value: string): value is `0x${string}` {
    return /^0x[a-fA-F0-9]{40}$/.test(value);
  }

  async function getSelectedCollateralConfig(): Promise<{
    isCustom: boolean;
    address: `0x${string}`;
    decimals: number;
    symbol: string;
  }> {
    if (collateralMode === 'default') {
      const meta = await resolveCollateralMeta(DEPLOYMENT.collateral);
      return {
        isCustom: false,
        address: DEPLOYMENT.collateral as `0x${string}`,
        decimals: meta.decimals,
        symbol: meta.symbol,
      };
    }

    const customAddrRaw = customCollateralInput.value.trim();
    if (!isAddress(customAddrRaw) || customAddrRaw.toLowerCase() === ZERO_ADDRESS) {
      throw new Error('Enter a valid custom collateral token address.');
    }

    const [symbolRead, decimalsRead] = await Promise.all([
      readClient.readContract({
        address: customAddrRaw,
        abi: erc20Abi,
        functionName: 'symbol',
      }),
      readClient.readContract({
        address: customAddrRaw,
        abi: erc20Abi,
        functionName: 'decimals',
      }),
    ]);

    const decimals = Number(decimalsRead);
    if (decimals !== 18) {
      throw new Error('Custom collateral must use 18 decimals.');
    }

    return {
      isCustom: true,
      address: customAddrRaw,
      decimals,
      symbol: String(symbolRead || 'TOKEN'),
    };
  }

  async function refreshCreateBalances() {
    const liqRaw = liquidityInput.value.trim() || '0';

    let collateralConfig: { isCustom: boolean; address: `0x${string}`; decimals: number; symbol: string };
    try {
      collateralConfig = await getSelectedCollateralConfig();
      await updateCreateCollateralBadge(collateralConfig.address, collateralConfig.symbol);
    } catch (error: any) {
      walletCollateralLabelEl.textContent = 'Wallet Token';
      requiredCollateralLabelEl.textContent = 'Required Collateral';
      factoryAllowanceLabelEl.textContent = 'Factory Allowance';
      requiredCollateralSymbolEl.textContent = collateralMode === 'custom' ? 'TOKEN' : 'Collateral';
      createCollateralBadgeSymbolEl.textContent = collateralMode === 'custom' ? 'TOKEN' : 'COLLATERAL';
      createCollateralLogoEl.src = `${import.meta.env.BASE_URL}images/coin.svg`;
      createCollateralLogoEl.alt = 'Collateral';
      walletUsdcCreateEl.textContent = '-';
      factoryAllowanceCreateEl.textContent = '-';
      requiredCollateralCreateEl.textContent = '-';
      createFundingStatusEl.textContent = error?.message || 'Invalid collateral settings';
      createFundingStatusEl.title = error?.message || 'Invalid collateral settings';
      return;
    }

    const liquidity = parseUnits(liqRaw, collateralConfig.decimals);
    const requiredCollateral = liquidity * 2n;
    walletCollateralLabelEl.textContent = `Wallet ${collateralConfig.symbol}`;
    requiredCollateralLabelEl.textContent = `Required ${collateralConfig.symbol}`;
    factoryAllowanceLabelEl.textContent = `${collateralConfig.symbol} Allowance`;
    requiredCollateralSymbolEl.textContent = collateralConfig.symbol;
    requiredCollateralCreateEl.textContent = `${fmtToken(requiredCollateral, collateralConfig.decimals)} ${collateralConfig.symbol}`;

    if (!walletState.account) {
      walletUsdcCreateEl.textContent = '-';
      factoryAllowanceCreateEl.textContent = '-';
      createFundingStatusEl.textContent = 'Connect wallet';
      createFundingStatusEl.title = 'Connect wallet to view balance and allowance.';
      return;
    }

    try {
      const [walletUsdc, allowance] = await Promise.all([
        readClient.readContract({
          address: collateralConfig.address,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [walletState.account as `0x${string}`],
        }),
        readClient.readContract({
          address: collateralConfig.address,
          abi: erc20Abi,
          functionName: 'allowance',
          args: [walletState.account as `0x${string}`, DEPLOYMENT.factory as `0x${string}`],
        }),
      ]);

      const walletUsdcBn = walletUsdc as bigint;
      const allowanceBn = allowance as bigint;

      walletUsdcCreateEl.textContent = `${fmtToken(walletUsdcBn, collateralConfig.decimals)} ${collateralConfig.symbol}`;
      factoryAllowanceCreateEl.textContent = allowanceBn >= requiredCollateral && requiredCollateral > 0n
        ? 'Ready'
        : `${fmtToken(allowanceBn, collateralConfig.decimals)} ${collateralConfig.symbol}`;

      if (walletUsdcBn < requiredCollateral) {
        createFundingStatusEl.textContent = 'Insufficient balance';
        createFundingStatusEl.title = `Wallet ${collateralConfig.symbol} is below required 2x initial liquidity collateral.`;
      } else if (allowanceBn < requiredCollateral) {
        createFundingStatusEl.textContent = 'Approval needed';
        createFundingStatusEl.title = `${collateralConfig.symbol} allowance is below required collateral. Submit will request approval first.`;
      } else {
        createFundingStatusEl.textContent = 'Ready';
        createFundingStatusEl.title = 'Funding checks pass for market creation.';
      }
    } catch {
      walletUsdcCreateEl.textContent = '-';
      factoryAllowanceCreateEl.textContent = '-';
      createFundingStatusEl.textContent = 'Status unavailable';
      createFundingStatusEl.title = 'Unable to fetch balance/allowance from chain.';
    }
  }

  async function runTxConfirmationModal(config: {
    title: string;
    bodyLines: string[];
    submitLabel: string;
    submitButtonClass?: string;
    submitAction: () => Promise<void>;
    approval?: {
      label: string;
      checkNeeded: () => Promise<boolean>;
      approveAction: () => Promise<void>;
    };
  }): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const overlay = document.createElement('div');
      overlay.className = 'confirm-modal-overlay';
      overlay.innerHTML = `
        <div class="confirm-modal-card card" role="dialog" aria-modal="true" aria-label="${config.title}">
          <div class="confirm-modal-title">${config.title}</div>
          <div class="confirm-modal-body" style="display:grid; gap:10px;">
            <div style="display:grid; gap:6px;">${config.bodyLines.map((line) => `<div>${line}</div>`).join('')}</div>
            <div id="txFlowStatus" class="tx-flow-status">Checking requirements...</div>
          </div>
          <div class="confirm-modal-actions">
            <button type="button" class="btn" data-action="cancel">Cancel</button>
            ${config.approval ? `<button type="button" class="btn btn-amber" data-action="approve">${config.approval.label}</button>` : ''}
            <button type="button" class="btn ${config.submitButtonClass || 'btn-primary'}" data-action="submit">${config.submitLabel}</button>
          </div>
        </div>
      `;

      document.body.appendChild(overlay);

      const statusEl = overlay.querySelector('#txFlowStatus') as HTMLDivElement;
      const cancelBtn = overlay.querySelector('[data-action="cancel"]') as HTMLButtonElement;
      const approveBtn = overlay.querySelector('[data-action="approve"]') as HTMLButtonElement | null;
      const submitBtn = overlay.querySelector('[data-action="submit"]') as HTMLButtonElement;

      let approvalStillNeeded = false;

      const cleanup = () => {
        overlay.remove();
      };

      const setBusy = (button: HTMLButtonElement, busy: boolean, idleLabel: string) => {
        button.disabled = busy;
        button.innerHTML = busy
          ? `<span class="tx-btn-spinner" aria-hidden="true"></span>${idleLabel}`
          : idleLabel;
      };

      const fail = (error: any) => {
        cleanup();
        reject(error);
      };

      const refreshApprovalState = async () => {
        if (!config.approval) {
          submitBtn.disabled = false;
          statusEl.textContent = 'Ready to submit.';
          return;
        }

        approvalStillNeeded = await config.approval.checkNeeded();
        if (approvalStillNeeded) {
          submitBtn.disabled = true;
          statusEl.textContent = 'Approval required before submit.';
          if (approveBtn) {
            approveBtn.style.display = '';
            approveBtn.disabled = false;
          }
          return;
        }

        submitBtn.disabled = false;
        statusEl.textContent = 'Approval satisfied. Ready to submit.';
        if (approveBtn) {
          approveBtn.style.display = 'none';
        }
      };

      cancelBtn.addEventListener('click', () => {
        cleanup();
        reject(new Error('Transaction cancelled.'));
      });

      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
          cleanup();
          reject(new Error('Transaction cancelled.'));
        }
      });

      approveBtn?.addEventListener('click', async () => {
        if (!config.approval) return;
        try {
          setBusy(approveBtn, true, config.approval.label);
          submitBtn.disabled = true;
          statusEl.textContent = 'Sending approval transaction...';
          await config.approval.approveAction();
          setBusy(approveBtn, false, config.approval.label);
          await refreshApprovalState();
        } catch (error: any) {
          setBusy(approveBtn, false, config.approval.label);
          fail(error);
        }
      });

      submitBtn.addEventListener('click', async () => {
        try {
          if (config.approval && approvalStillNeeded) {
            statusEl.textContent = 'Approval must be completed first.';
            return;
          }
          setBusy(submitBtn, true, config.submitLabel);
          cancelBtn.disabled = true;
          if (approveBtn) approveBtn.disabled = true;
          statusEl.textContent = 'Submitting transaction...';
          await config.submitAction();
          cleanup();
          resolve();
        } catch (error: any) {
          fail(error);
        }
      });

      refreshApprovalState().catch(fail);
    });
  }

  async function runMarketCreatedModal(marketAddress: `0x${string}`): Promise<void> {
    await new Promise<void>((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'confirm-modal-overlay';
      overlay.innerHTML = `
        <div class="confirm-modal-card card" role="dialog" aria-modal="true" aria-label="Market created">
          <div class="confirm-modal-title">Market Created</div>
          <div class="confirm-modal-body" style="display:grid; gap:10px;">
            <div>Your market is live.</div>
            <div style="display:grid; gap:6px; border:1px solid rgba(30,61,90,.75); background:rgba(8,19,30,.62); padding:8px; border-radius:2px;">
              <div class="mono" style="font-size:.62rem; color:var(--muted);">Contract Address</div>
              <div class="mono" style="word-break:break-all; font-size:.72rem; color:#dff4ff;">${marketAddress}</div>
            </div>
          </div>
          <div class="confirm-modal-actions">
            <button type="button" class="btn" data-action="copy">Copy CA</button>
            <button type="button" class="btn btn-primary" data-action="open">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style="width:14px; height:14px; margin-right:6px; vertical-align:-2px;">
                <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v17H6.5A2.5 2.5 0 0 1 4 17.5z" fill="none" stroke="currentColor" stroke-width="1.8"/>
                <path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H13v17h4.5a2.5 2.5 0 0 0 2.5-2.5z" fill="none" stroke="currentColor" stroke-width="1.8"/>
                <path d="M11 6.5h2M11 10h2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
              Market Page
            </button>
            <button type="button" class="btn" data-action="close">Back to Markets</button>
          </div>
        </div>
      `;

      document.body.appendChild(overlay);

      const copyBtn = overlay.querySelector('[data-action="copy"]') as HTMLButtonElement;
      const openBtn = overlay.querySelector('[data-action="open"]') as HTMLButtonElement;
      const closeBtn = overlay.querySelector('[data-action="close"]') as HTMLButtonElement;

      const cleanup = () => {
        overlay.remove();
      };

      copyBtn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(marketAddress);
          notify('ok', 'Market address copied');
        } catch {
          notify('warn', 'Copy failed. You can copy the address from the modal.');
        }
      });

      openBtn.addEventListener('click', () => {
        cleanup();
        window.location.href = `${import.meta.env.BASE_URL}?market=${marketAddress}`;
        resolve();
      });

      closeBtn.addEventListener('click', () => {
        cleanup();
        window.location.href = './';
        resolve();
      });
    });
  }

  liquidityInput.addEventListener('input', async () => {
    updateRequiredCollateralLabel();
    await refreshCreateBalances();
  });

  collateralDefaultBtn.addEventListener('click', async () => {
    setCollateralMode('default');
    updateRequiredCollateralLabel();
    await refreshCreateBalances();
  });

  collateralCustomBtn.addEventListener('click', async () => {
    setCollateralMode('custom');
    updateRequiredCollateralLabel();
    await refreshCreateBalances();
  });

  customCollateralInput.addEventListener('input', async () => {
    if (collateralMode !== 'custom') return;
    await refreshCreateBalances();
  });

  connectBtn.addEventListener('click', async () => {
    if (walletState.account) {
      await openAccountModal({
        onWalletStateChange: async () => {
          setAccountLabel();
          await refreshCreateBalances();
        },
      });
      return;
    }

    try {
      await connectWallet();
      const chainOk = await ensurePulseChainConnected();
      setAccountLabel();
      if (!chainOk) {
        notify('warn', `Switch to ${getActiveChainConfig().chainName} to continue`);
        return;
      }
      notify('ok', `Wallet connected: ${walletState.account}`);
      await refreshCreateBalances();
    } catch (error: any) {
      notify('err', error?.message || 'Wallet connect failed');
    }
  });

  confirmCreateBtn.addEventListener('click', async () => {
    const q = qInput.value.trim();
    const yesName = yesNameInput.value.trim();
    const yesSymbol = yesSymbolInput.value.trim();
    const noName = noNameInput.value.trim();
    const noSymbol = noSymbolInput.value.trim();
    const h = Number(hoursInput.value || '24');
    const liqRaw = liquidityInput.value.trim();

    const encoder = new TextEncoder();
    const invalidLabel = [yesName, yesSymbol, noName, noSymbol].some((value) => {
      const len = encoder.encode(value).length;
      return len === 0 || len > MAX_OUTCOME_TOKEN_LABEL_BYTES;
    });

    if (!q) {
      notify('warn', 'BET is required');
      return;
    }
    if (invalidLabel) {
      notify('warn', 'Token names/symbols must be 1 to 25 UTF-8 bytes');
      return;
    }
    if (!liqRaw || Number(liqRaw) <= 0) {
      notify('warn', 'Initial liquidity must be greater than zero');
      return;
    }

    try {
      const collateralConfig = await getSelectedCollateralConfig();
      const liquidity = parseUnits(liqRaw, collateralConfig.decimals);
      const minInitialLiquidity = collateralConfig.isCustom
        ? MIN_INITIAL_LIQUIDITY_WHOLE_TOKENS * (10n ** BigInt(collateralConfig.decimals))
        : MIN_INITIAL_LIQUIDITY_DEFAULT;
      if (liquidity < minInitialLiquidity) {
        notify(
          'warn',
          `Initial liquidity too low. Minimum is ${fmtToken(minInitialLiquidity, collateralConfig.decimals)} ${collateralConfig.symbol}.`,
        );
        return;
      }

      const requiredCollateral = liquidity * 2n;
      const resolveAfter = Math.floor(Date.now() / 1000) + Math.max(1, h) * 3600;

      await runTxConfirmationModal({
        title: 'Create Market',
        bodyLines: [
          `BET: ${q}`,
          `Outcome 1: ${yesName} (${yesSymbol})`,
          `Outcome 2: ${noName} (${noSymbol})`,
          `Collateral: ${collateralConfig.isCustom ? `Custom ${collateralConfig.symbol} (${collateralConfig.address})` : `Default ${collateralConfig.symbol}`}`,
          `Resolve in: ${Math.max(1, h)} hours`,
          `Initial Liquidity: ${liqRaw} ${collateralConfig.symbol}`,
          `Required Collateral: ${fmtToken(requiredCollateral, collateralConfig.decimals)} ${collateralConfig.symbol}`,
        ],
        submitLabel: 'Create Market',
        submitButtonClass: 'btn-primary',
        approval: {
          label: `Approve ${collateralConfig.symbol}`,
          checkNeeded: async () => {
            if (!walletState.account) throw new Error('Connect wallet first');
            const allowance = await readClient.readContract({
              address: collateralConfig.address,
              abi: erc20Abi,
              functionName: 'allowance',
              args: [walletState.account as `0x${string}`, DEPLOYMENT.factory as `0x${string}`],
            });
            return (allowance as bigint) < requiredCollateral;
          },
          approveAction: async () => {
            const token = contractWithSigner(collateralConfig.address, erc20AbiEthers);
            const approveTx = await token.approve(DEPLOYMENT.factory, requiredCollateral);
            notify('warn', `Approve collateral tx: ${approveTx.hash}`);
            await approveTx.wait();
          },
        },
        submitAction: async () => {
          const marketCountBefore = await readClient.readContract({
            address: DEPLOYMENT.factory as `0x${string}`,
            abi: factoryAbi,
            functionName: 'marketCount',
          }) as bigint;

          const factory = contractWithSigner(DEPLOYMENT.factory, factoryAbiEthers);
          const tx = collateralConfig.isCustom
            ? await factory['createMarket(address,string,uint64,uint256,string,string,string,string)'](
              collateralConfig.address,
              q,
              resolveAfter,
              liquidity,
              yesName,
              yesSymbol,
              noName,
              noSymbol,
            )
            : await factory['createMarket(string,uint64,uint256,string,string,string,string)'](
              q,
              resolveAfter,
              liquidity,
              yesName,
              yesSymbol,
              noName,
              noSymbol,
            );
          notify('warn', `Create tx submitted: ${tx.hash}`);
          await tx.wait();
          notify('ok', 'Market created');

          let createdMarket: `0x${string}` | null = null;
          try {
            const marketAddress = await readClient.readContract({
              address: DEPLOYMENT.factory as `0x${string}`,
              abi: factoryAbi,
              functionName: 'markets',
              args: [marketCountBefore],
            });
            const marketAddressText = String(marketAddress || '');
            if (isAddress(marketAddressText)) {
              createdMarket = marketAddressText;
            }
          } catch {
            // Fall back to redirect if market lookup fails.
          }

          if (createdMarket) {
            await runMarketCreatedModal(createdMarket);
            return;
          }

          window.location.href = './';
        },
      });
    } catch (error: any) {
      notify('err', friendlyErrorMessage(error, 'Create failed'));
    }
  });

  bindWalletEvents(async () => {
    setAccountLabel();
    if (walletState.account) {
      await ensurePulseChainConnected();
    }
    await refreshCreateBalances();
  });

  void rehydrateWalletSession().then(() => {
    setAccountLabel();
    if (walletState.account) {
      void ensurePulseChainConnected();
    }
    void refreshCreateBalances();
  }).catch(() => {
    // no-op
  });

  setAccountLabel();
  setCollateralMode('default');
  updateRequiredCollateralLabel();
  void refreshCreateBalances();
}
